import React from "react";
import { motion } from "framer-motion";

export default function Tools({ setActiveTab }) {
  const techCategories = [
    {
      category: "Shopify Development",
      icon: "🛒",
      technologies: [
        { name: "Liquid Templating", level: 98, icon: "💧" },
        { name: "Shopify Themes", level: 96, icon: "🎨" },
        { name: "Shopify CLI", level: 95, icon: "⚙️" },
        { name: "Theme Kit", level: 92, icon: "🔧" },
        { name: "Shopify Apps", level: 90, icon: "🧩" },
        { name: "Checkout Extensions", level: 88, icon: "🛍️" }
      ]
    },
    {
      category: "Frontend Technologies",
      icon: "💻",
      technologies: [
        { name: "HTML5", level: 98, icon: "🌐" },
        { name: "CSS3", level: 98, icon: "🎨" },
        { name: "JavaScript", level: 95, icon: "🟨" },
        { name: "Bootstrap", level: 92, icon: "🅱️" },
        { name: "SCSS/Sass", level: 90, icon: "🎯" },
        { name: "jQuery", level: 88, icon: "📜" }
      ]
    },
    {
      category: "CMS & E-commerce Platforms",
      icon: "🌐",
      technologies: [
        { name: "WordPress", level: 95, icon: "📝" },
        { name: "WooCommerce", level: 92, icon: "🛒" },
        { name: "Webflow", level: 90, icon: "🌊" },
        { name: "BigCommerce", level: 85, icon: "🏪" },
        { name: "Contentful", level: 82, icon: "📄" },
        { name: "Umbraco", level: 80, icon: "🔷" }
      ]
    },
    {
      category: "Design to Development",
      icon: "🎨",
      technologies: [
        { name: "Figma to HTML", level: 95, icon: "🎨" },
        { name: "PSD to HTML", level: 92, icon: "🖼️" },
        { name: "Adobe XD", level: 88, icon: "🔷" },
        { name: "Responsive Design", level: 96, icon: "📱" },
        { name: "Cross-browser Compatibility", level: 94, icon: "🌍" },
        { name: "Pixel Perfect Conversion", level: 93, icon: "🎯" }
      ]
    },
    {
      category: "No-Code Platforms",
      icon: "⚡",
      technologies: [
        { name: "Wix Editor", level: 88, icon: "🎯" },
        { name: "Squarespace", level: 85, icon: "⬜" },
        { name: "Framer Sites", level: 82, icon: "🖼️" },
        { name: "Elementor", level: 90, icon: "🧱" },
        { name: "Webflow Designer", level: 88, icon: "🌊" },
        { name: "Divi Builder", level: 80, icon: "🏗️" }
      ]
    },
    {
      category: "Development Tools",
      icon: "🛠️",
      technologies: [
        { name: "VS Code", level: 98, icon: "💻" },
        { name: "GitHub", level: 95, icon: "🐱" },
        { name: "Git", level: 94, icon: "📦" },
        { name: "Chrome DevTools", level: 92, icon: "🔍" },
        { name: "Figma", level: 90, icon: "🎨" },
        { name: "Adobe Photoshop", level: 85, icon: "🖼️" }
      ]
    },
    {
      category: "Modern Frameworks",
      icon: "⚛️",
      technologies: [
        { name: "React", level: 88, icon: "⚛️" },
        { name: "Tailwind CSS", level: 90, icon: "💨" },
        { name: "Framer Motion", level: 85, icon: "🎭" },
        { name: "Node.js", level: 80, icon: "🟢" },
        { name: "REST APIs", level: 85, icon: "🔄" },
        { name: "GraphQL", level: 78, icon: "📊" }
      ]
    }
  ];

  const containerVariants = {
    initial: {},
    animate: {
      transition: {
        staggerChildren: 0.3
      }
    }
  };

  const categoryVariants = {
    initial: { opacity: 0, y: 50 },
    animate: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const techGridVariants = {
    initial: {},
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const techCardVariants = {
    initial: { opacity: 0, scale: 0.9, y: 20 },
    animate: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  const progressBarVariants = {
    initial: { width: 0 },
    animate: (level) => ({
      width: `${level}%`,
      transition: {
        duration: 1.5,
        ease: "easeOut",
        delay: 0.3
      }
    })
  };



  return (
    <motion.div 
      className="max-w-4xl mx-auto"
      variants={containerVariants}
      initial="initial"
      animate="animate"
    >
      <motion.div 
        className="text-center mb-8 sm:mb-12"
        variants={categoryVariants}
      >
        <motion.h2 
          className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-3 sm:mb-4"
          whileHover={{ scale: 1.02 }}
        >
          Technology Stack & Tools
        </motion.h2>
        <p className="text-sm sm:text-base md:text-lg text-gray-600">
          Comprehensive toolkit for building high-performing e-commerce stores and web applications
        </p>
      </motion.div>

      <div className="space-y-12">
        {techCategories.map((category, categoryIndex) => (
          <motion.div 
            key={categoryIndex} 
            className="bg-gray-50 rounded-2xl p-6 sm:p-8"
            variants={categoryVariants}
            whileHover={{ 
              scale: 1.01,
              transition: { duration: 0.2 }
            }}
          >
            <motion.div 
              className="flex items-center mb-8"
              whileHover={{ x: 5 }}
              transition={{ duration: 0.2 }}
            >
              <motion.div 
                className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mr-4 shadow-sm"
                whileHover={{ 
                  rotate: 360,
                  scale: 1.1,
                  transition: { duration: 0.6 }
                }}
              >
                <span className="text-2xl">{category.icon}</span>
              </motion.div>
              <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-900">
                {category.category}
              </h3>
            </motion.div>

            <motion.div 
              className="grid grid-cols-1 sm:grid-cols-2 gap-6"
              variants={techGridVariants}
            >
              {category.technologies.map((tech, techIndex) => (
                <motion.div
                  key={techIndex}
                  className="bg-white rounded-xl p-6 hover:shadow-md transition-shadow"
                  variants={techCardVariants}
                  whileHover={{ 
                    y: -3,
                    scale: 1.02,
                    transition: { duration: 0.2 }
                  }}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <motion.span 
                        className="text-xl mr-3"
                        whileHover={{ 
                          scale: 1.3,
                          rotate: 20,
                          transition: { duration: 0.3 }
                        }}
                      >
                        {tech.icon}
                      </motion.span>
                      <span className="font-semibold text-sm sm:text-base text-gray-900">
                        {tech.name}
                      </span>
                    </div>
                    <motion.span 
                      className="text-sm font-medium text-gray-500"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.5 }}
                    >
                      {tech.level}%
                    </motion.span>
                  </div>
                  
                  <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                    <motion.div
                      className="bg-gradient-to-r from-purple-500 to-purple-600 h-2 rounded-full"
                      variants={progressBarVariants}
                      custom={tech.level}
                      initial="initial"
                      animate="animate"
                    />
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        ))}
      </div>

      {/* Learning Section */}
      <motion.div 
                  className="mt-12 p-8 bg-gradient-vibrant rounded-2xl shadow-lg"
        variants={categoryVariants}
        whileHover={{ 
          scale: 1.01,
          transition: { duration: 0.3 }
        }}
      >
        <div className="text-center">
          <motion.h3 
            className="text-xl font-semibold text-gray-900 mb-4"
            whileHover={{ scale: 1.02 }}
          >
            Currently Exploring
          </motion.h3>
          <div className="flex flex-wrap justify-center gap-3 mb-6">
            {["Hydrogen", "Remix", "Shopify Functions", "Flow", "Checkout Extensions", "Web Pixel API"].map((skill, index) => (
              <motion.span
                key={skill}
                className="px-4 py-2 bg-white rounded-full text-sm font-medium text-gray-700 shadow-sm border border-gray-200 cursor-pointer"
                initial={{ opacity: 0, y: 20 }}
                animate={{ 
                  opacity: 1, 
                  y: 0,
                  transition: { delay: 0.1 * index, duration: 0.3 }
                }}
                whileHover={{ 
                  scale: 1.15,
                  y: -4,
                  boxShadow: "0 8px 20px rgba(139, 92, 246, 0.3)",
                  backgroundColor: "#8b5cf6",
                  color: "white",
                  borderColor: "#8b5cf6"
                }}
                whileTap={{ scale: 0.95 }}
                transition={{ 
                  type: "spring",
                  stiffness: 400,
                  damping: 17
                }}
              >
                {skill}
              </motion.span>
            ))}
          </div>
          <motion.p 
            className="text-gray-600"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
          >
            Staying ahead with the latest web technologies and e-commerce innovations across multiple platforms to deliver cutting-edge solutions.
          </motion.p>
        </div>
      </motion.div>

      {/* CTA Section */}
      <motion.div 
                  className="text-center mt-12 p-8 bg-gradient-dark text-white rounded-2xl glow-on-hover"
        variants={categoryVariants}
        whileHover={{ 
          scale: 1.01,
          transition: { duration: 0.3 }
        }}
      >
        <motion.h3 
          className="text-xl font-semibold mb-4"
          whileHover={{ scale: 1.02 }}
        >
          Ready to Build Your Digital Solution?
        </motion.h3>
        <motion.p 
          className="text-gray-300 mb-6"
          initial={{ opacity: 0.8 }}
          whileHover={{ opacity: 1 }}
        >
          Let's discuss your project goals and create a high-converting digital solution that scales with your business.
        </motion.p>
        <motion.button
          onClick={() => setActiveTab("Projects")}
                      className="btn-gradient inline-flex items-center"
          whileHover={{ 
            scale: 1.05,
            boxShadow: "0 15px 35px rgba(96, 165, 250, 0.4)",
            transition: { duration: 0.2 }
          }}
          whileTap={{ scale: 0.95 }}
        >
          Start Your Project
          <motion.svg 
            className="ml-2 w-4 h-4" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
            initial={{ x: 0 }}
            whileHover={{ 
              x: 3,
              transition: { duration: 0.2 }
            }}
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
          </motion.svg>
        </motion.button>
      </motion.div>
    </motion.div>
  );
} 